import classNames from 'classnames';
import React from 'react';
import * as Splashes from '@/assets/graphics/splashes/splashes';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import styles from './splash.module.scss';

export interface SplashProps {
  /** The class name to apply */
  className?: string;
  /** The highlighted text to display */
  highlightedText?: Content.Components['SplashDto']['highlightedText'];
  /** The image to replace the whole splash */
  image?: Content.Components['SplashDto']['image'];
  /** The name of the splash to show */
  splashTheme?: Content.Components['SplashDto']['splashTheme'];
  /** The type of splash to show */
  splashType?: Content.Components['SplashDto']['splashType'];
  /** The text to display inside the splash */
  text?: Content.Components['SplashDto']['text'];
}

const Splash = ({
  className,
  highlightedText,
  image,
  splashTheme = 'primary',
  splashType = 'normal',
  text,
}: SplashProps) => {
  const isImageSplash =
    image && (splashType === 'image' || splashType === 'highlightedImage');

  const isHighlighted =
    (highlightedText && splashType === 'highlighted') ||
    (image && splashType === 'highlightedImage');

  const splashSize =
    splashType === 'highlighted' || splashType === 'highlightedImage'
      ? 'Large'
      : 'Small';

  const name = `${capitalizeFirstLetter(splashTheme)}${splashSize}`;

  const SplashComponent = Splashes[name];

  if (!SplashComponent) return null;

  if (splashType === 'highlighted' && !text && !highlightedText) return null;

  if (splashType === 'normal' && !text) return null;

  if (splashType === 'image' && !image) return null;

  if (splashType === 'highlightedImage' && !image) return null;

  const renderContent = () => {
    if (isImageSplash) {
      return (
        <Image
          alt={image.altText}
          focalPoint={image.focalPoint}
          height={240}
          priority
          src={image.url}
          title={image.name}
          unoptimized
          width={240}
        />
      );
    }

    return (
      <React.Fragment>
        <SplashComponent className={styles.splash} />

        {splashType === 'normal' && text && (
          <Typography className={styles.text} tagStyle="splash">
            {text}
          </Typography>
        )}

        {splashType === 'highlighted' && (
          <React.Fragment>
            <div className={styles.textWrapper}>
              {text && (
                <Typography className={styles.text} tagStyle="labelMedium">
                  {text}
                </Typography>
              )}

              {highlightedText && (
                <Typography className={styles.text} tagStyle="splashOffer">
                  {highlightedText}
                </Typography>
              )}
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isLarge]: splashSize === 'Large',
        [styles.isSmall]: splashSize === 'Small',
        [styles.isPrimary]: splashTheme === 'primary',
        [styles.isSecondary]: splashTheme === 'secondary',
        // Using this theme is conditional since we have different splash themes for different brands.
        [styles.isTertiary]: splashTheme === ('tertiary' as unknown),
        [styles.isHighlighted]: isHighlighted,
        [styles.isImage]: isImageSplash,
      })}
    >
      {renderContent()}
    </div>
  );
};

Splash.displayName = 'Splash';

export default Splash;
